import React, { useMemo, useState } from 'react';
import { format, startOfWeek, addDays, addWeeks, subWeeks } from 'date-fns';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../ui/Button';
import { Card, CardContent, CardHeader } from '../ui/Card';
import type { ScheduleOccurrence } from '../../hooks/useSchedule';
import {
  appendSchedulePastOpacity,
  getScheduleOccurrenceSlotStyleProps,
  isScheduleOccurrencePast,
  POSTPONED_SCHEDULE_SLOT_CLASSES,
} from '../../utils/lessonSlotColors';
import { useAgeGroupLookups } from '../../queries/ageGroups';
import { getOccurrenceScopeLabel } from '../../utils/bookingScopeLabel';
import { getStackedTimeSlotStyle } from '../../utils/scheduleOverlapLayout';
import { formatTime, timeToMinutes, minutesToTime } from '../../utils/timeFormat';
import {
  COURT_HIRE_EXTRA_EVENT_CHIP_CLASSES,
  extraEventStackKey,
  formatConsumerCalendarEquipmentSummary,
  type ConsumerCalendarExtraEvent,
} from '@/utils/consumerCalendarEvents';

const DAYS_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function occurrenceStackKey(occ: ScheduleOccurrence): string {
  return `${occ.bookingId}-${format(occ.date, 'yyyy-MM-dd')}`;
}

interface BookingWeeklyScheduleProps {
  occurrences: ScheduleOccurrence[];
  termStart?: Date;
  termEnd?: Date;
  onOccurrenceClick?: (occurrence: ScheduleOccurrence) => void;
  isMakeup?: (occurrence: ScheduleOccurrence) => boolean;
  /** Map of studentId -> display name, for showing "For: Student Name" in cells */
  studentsById?: Record<string, string>;
  /** Renders in the calendar card toolbar (e.g. location filter) */
  toolbarTrailing?: React.ReactNode;
  extraEvents?: ConsumerCalendarExtraEvent[];
  onExtraEventClick?: (event: ConsumerCalendarExtraEvent) => void;
}

export function BookingWeeklySchedule({
  occurrences,
  termStart,
  termEnd,
  onOccurrenceClick,
  isMakeup,
  studentsById,
  toolbarTrailing,
  extraEvents = [],
  onExtraEventClick,
}: BookingWeeklyScheduleProps) {
  const academyId = occurrences[0]?.academyId;
  const { ageGroupById } = useAgeGroupLookups(academyId);

  const [displayWeekStart, setDisplayWeekStart] = useState(() => {
    const now = new Date();
    const weekStart = startOfWeek(now, { weekStartsOn: 1 }); // Monday
    if (termStart && termEnd) {
      if (now < termStart) return startOfWeek(termStart, { weekStartsOn: 1 });
      if (now > termEnd) return startOfWeek(termEnd, { weekStartsOn: 1 });
    }
    return weekStart;
  });

  const displayWeekEnd = addDays(displayWeekStart, 6);
  const canPrev = !termStart || displayWeekStart > termStart;
  const canNext = !termEnd || displayWeekEnd < termEnd;

  const occurrencesInWeek = useMemo(() => {
    return occurrences.filter((occ) => {
      const d = occ.date;
      return d >= displayWeekStart && d <= displayWeekEnd;
    });
  }, [occurrences, displayWeekStart, displayWeekEnd]);

  const extraEventsInWeek = useMemo(() => {
    return extraEvents.filter((event) => {
      const d = event.date;
      return d >= displayWeekStart && d <= displayWeekEnd;
    });
  }, [extraEvents, displayWeekStart, displayWeekEnd]);

  const { earliestTime, latestTime } = useMemo(() => {
    const timedItems = [
      ...occurrencesInWeek.map((occ) => ({ startTime: occ.startTime, endTime: occ.endTime })),
      ...extraEventsInWeek.map((event) => ({ startTime: event.startTime, endTime: event.endTime })),
    ];
    if (timedItems.length === 0) {
      return { earliestTime: 8 * 60, latestTime: 20 * 60 };
    }
    let earliest = 24 * 60;
    let latest = 0;
    timedItems.forEach((item) => {
      const s = timeToMinutes(item.startTime);
      const e = timeToMinutes(item.endTime);
      if (s < earliest) earliest = s;
      if (e > latest) latest = e;
    });
    earliest = Math.max(0, Math.floor(earliest / 30) * 30 - 60);
    latest = Math.min(24 * 60, Math.ceil(latest / 30) * 30 + 60);
    return { earliestTime: earliest, latestTime: latest };
  }, [occurrencesInWeek, extraEventsInWeek]);

  const timeSlots = useMemo(() => {
    const slots: number[] = [];
    for (let m = earliestTime; m <= latestTime; m += 30) slots.push(m);
    return slots;
  }, [earliestTime, latestTime]);

  const occurrencesByDate = useMemo(() => {
    const map = new Map<string, ScheduleOccurrence[]>();
    occurrencesInWeek.forEach((occ) => {
      const dateKey = format(occ.date, 'yyyy-MM-dd');
      if (!map.has(dateKey)) map.set(dateKey, []);
      map.get(dateKey)!.push(occ);
    });
    return map;
  }, [occurrencesInWeek]);

  const extraEventsByDate = useMemo(() => {
    const map = new Map<string, ConsumerCalendarExtraEvent[]>();
    extraEventsInWeek.forEach((event) => {
      const dateKey = format(event.date, 'yyyy-MM-dd');
      if (!map.has(dateKey)) map.set(dateKey, []);
      map.get(dateKey)!.push(event);
    });
    return map;
  }, [extraEventsInWeek]);

  const totalRange = latestTime - earliestTime;

  const getOccurrenceChipProps = (occ: ScheduleOccurrence) => {
    const makeup = isMakeup?.(occ) ?? false;
    const isPast = isScheduleOccurrencePast(occ.date, occ.endTime);

    if (occ.status === 'cancelled') {
      return {
        className:
          'border border-red-300 dark:border-red-700 border-l-4 border-l-red-500 bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200 line-through opacity-75',
      };
    }
    if (occ.status === 'postponed') {
      return { className: appendSchedulePastOpacity(POSTPONED_SCHEDULE_SLOT_CLASSES, isPast) };
    }

    if (makeup) {
      return {
        className: appendSchedulePastOpacity(
          'border border-purple-400 dark:border-purple-600 border-l-4 border-l-purple-500 border-dashed bg-purple-50 dark:bg-purple-900/30 text-purple-900 dark:text-purple-100',
          isPast
        ),
      };
    }

    const styleProps = getScheduleOccurrenceSlotStyleProps(
      {
        ballColor: occ.ballColor,
        ageGroupId: occ.ageGroupId,
        lessonType: occ.lessonType as 'group' | 'private' | 'camp',
      },
      ageGroupById,
      isPast
    );
    return { className: styleProps.className, style: styleProps.style };
  };

  return (
    <div className="w-full overflow-x-auto">
      <Card className="overflow-hidden">
        <CardHeader className="mb-0 flex w-full flex-col gap-3 p-3 pb-3 sm:mb-4 sm:p-6 sm:pb-4">
          <div className="flex w-full flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
            <div className="flex min-w-0 items-center gap-1 sm:gap-2">
              <div
                className="inline-flex shrink-0 items-center overflow-hidden rounded-lg border border-border bg-background shadow-sm"
                role="group"
                aria-label="Calendar week navigation"
              >
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 shrink-0 rounded-none"
                  onClick={() => setDisplayWeekStart((w) => subWeeks(w, 1))}
                  disabled={!canPrev}
                  aria-label="Previous week"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <div className="flex h-8 shrink-0 items-center gap-1.5 border-x border-border px-2 sm:gap-2 sm:px-2.5">
                  <span
                    className="inline-flex shrink-0 items-center justify-center whitespace-nowrap text-sm font-semibold sm:text-base"
                    aria-live="polite"
                  >
                    {format(displayWeekStart, 'MMM d')} – {format(displayWeekEnd, 'MMM d, yyyy')}
                  </span>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 shrink-0 rounded-none"
                  onClick={() => setDisplayWeekStart((w) => addWeeks(w, 1))}
                  disabled={!canNext}
                  aria-label="Next week"
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
            {toolbarTrailing ? (
              <div className="flex w-full min-w-0 sm:ml-auto sm:w-auto sm:justify-end">
                {toolbarTrailing}
              </div>
            ) : null}
          </div>
        </CardHeader>
        <CardContent className="p-0">
      <div className="border-t border-border overflow-hidden">
        <div className="grid grid-cols-8">
          <div className="bg-gray-100 dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 p-2">
            <div className="font-semibold text-gray-900 dark:text-white text-sm">Time</div>
          </div>
          {[0, 1, 2, 3, 4, 5, 6].map((colIndex) => {
            const dayDate = addDays(displayWeekStart, colIndex);
            return (
              <div
                key={colIndex}
                className="bg-gray-100 dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 last:border-r-0 p-2 text-center"
              >
                <div className="font-semibold text-gray-900 dark:text-white text-sm">
                  {DAYS_SHORT[dayDate.getDay()]}
                </div>
                <div className="text-xs text-gray-600 dark:text-gray-400">
                  {format(dayDate, 'MMM d')}
                </div>
              </div>
            );
          })}
        </div>

        <div className="grid grid-cols-8 relative" style={{ minHeight: `${(totalRange / 30) * 36}px` }}>
          <div className="border-r border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 sticky left-0 z-10 relative">
            {timeSlots.filter((_, i) => i % 2 === 0).map((slotM) => {
              const pos = ((slotM - earliestTime) / totalRange) * 100;
              return (
                <div
                  key={slotM}
                  className="absolute left-0 right-0 px-1 text-xs text-gray-600 dark:text-gray-400"
                  style={{ top: `${pos}%` }}
                >
                  {formatTime(minutesToTime(slotM))}
                </div>
              );
            })}
          </div>

          {[0, 1, 2, 3, 4, 5, 6].map((colIndex) => {
            const dayDate = addDays(displayWeekStart, colIndex);
            const dateKey = format(dayDate, 'yyyy-MM-dd');
            const dayOccurrences = occurrencesByDate.get(dateKey) ?? [];
            const dayExtraEvents = extraEventsByDate.get(dateKey) ?? [];
            const columnItems = [
              ...dayOccurrences,
              ...dayExtraEvents,
            ] as Array<ScheduleOccurrence | ConsumerCalendarExtraEvent>;

            return (
              <div
                key={colIndex}
                className="border-r border-gray-200 dark:border-gray-700 last:border-r-0 relative bg-white dark:bg-gray-900"
              >
                {timeSlots.map((slotM) => {
                  const pos = ((slotM - earliestTime) / totalRange) * 100;
                  return (
                    <div
                      key={slotM}
                      className="absolute left-0 right-0 border-b border-gray-100 dark:border-gray-800"
                      style={{ top: `${pos}%` }}
                    />
                  );
                })}

                {dayOccurrences.map((occ) => {
                  const style = getStackedTimeSlotStyle(
                    occ,
                    columnItems,
                    (item) =>
                      'bookingId' in item
                        ? occurrenceStackKey(item)
                        : extraEventStackKey(item),
                    earliestTime,
                    latestTime
                  );
                  const makeup = isMakeup?.(occ) ?? false;
                  const scopeLabel = getOccurrenceScopeLabel({
                    bookingScope: occ.bookingScope,
                    lessonType: occ.lessonType,
                  });
                  const chipProps = getOccurrenceChipProps(occ);
                  return (
                    <button
                      key={occurrenceStackKey(occ)}
                      type="button"
                      onClick={() => onOccurrenceClick?.(occ)}
                      className={`absolute rounded p-1.5 text-left text-xs font-medium border transition-all z-10 overflow-hidden flex flex-col min-h-0 min-w-0 leading-tight ${chipProps.className} ${onOccurrenceClick ? 'cursor-pointer' : ''}`}
                      style={{
                        top: style.top,
                        height: style.height,
                        left: style.left,
                        width: style.width,
                        ...chipProps.style,
                      }}
                    >
                      <div className="flex items-start gap-1 min-w-0 shrink-0">
                        <div className="truncate font-medium min-w-0 flex-1">{occ.lessonName}</div>
                        {scopeLabel && (
                          <span className="shrink-0 whitespace-nowrap rounded px-1 py-0.5 text-[10px] leading-tight bg-amber-200 dark:bg-amber-800 text-amber-900 dark:text-amber-100">
                            {scopeLabel}
                          </span>
                        )}
                        {makeup && (
                          <span className="shrink-0 whitespace-nowrap rounded px-1 py-0.5 text-[10px] leading-tight bg-purple-200 dark:bg-purple-800 text-purple-800 dark:text-purple-200">
                            Make-up
                          </span>
                        )}
                      </div>
                      <div className="truncate opacity-90 text-[10px] min-w-0 shrink-0">
                        {formatTime(occ.startTime)}
                      </div>
                      {occ.courtLabel && (
                        <div className="truncate opacity-85 text-[10px] min-w-0 shrink-0">{occ.courtLabel}</div>
                      )}
                      {((occ.studentName ?? studentsById?.[occ.studentId]) || occ.termName) && (
                        <div className="opacity-85 text-[10px] mt-0.5 space-y-0.5 min-w-0 overflow-hidden">
                          {(occ.studentName ?? studentsById?.[occ.studentId]) && (
                            <div className="truncate">For: {occ.studentName ?? studentsById?.[occ.studentId]}</div>
                          )}
                          {occ.termName && (
                            <div className="truncate">Term: {occ.termName}</div>
                          )}
                        </div>
                      )}
                    </button>
                  );
                })}
                {dayExtraEvents.map((event) => {
                  const style = getStackedTimeSlotStyle(
                    event,
                    columnItems,
                    (item) =>
                      'bookingId' in item
                        ? occurrenceStackKey(item)
                        : extraEventStackKey(item),
                    earliestTime,
                    latestTime
                  );
                  const isPast = isScheduleOccurrencePast(event.date, event.endTime);
                  const equipmentSummary = formatConsumerCalendarEquipmentSummary(event.equipmentHires);
                  return (
                    <button
                      key={extraEventStackKey(event)}
                      type="button"
                      onClick={() => onExtraEventClick?.(event)}
                      className={`absolute rounded p-1.5 text-left text-xs font-medium border transition-all z-10 overflow-hidden flex flex-col min-h-0 min-w-0 leading-tight ${COURT_HIRE_EXTRA_EVENT_CHIP_CLASSES} ${onExtraEventClick ? 'cursor-pointer' : ''} ${isPast ? 'opacity-60' : ''}`}
                      style={{
                        top: style.top,
                        height: style.height,
                        left: style.left,
                        width: style.width,
                      }}
                      aria-label={`${event.title} court hire${equipmentSummary ? ` with ${equipmentSummary}` : ''}`}
                    >
                      <div className="truncate font-medium min-w-0 flex-1">{event.title}</div>
                      <div className="truncate opacity-90 text-[10px] min-w-0 shrink-0">
                        {formatTime(event.startTime)}
                      </div>
                      {equipmentSummary ? (
                        <div className="truncate opacity-85 text-[10px] min-w-0 shrink-0">
                          {equipmentSummary}
                        </div>
                      ) : null}
                      <div className="truncate opacity-85 text-[10px] min-w-0 shrink-0">Court hire</div>
                    </button>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
        </CardContent>
      </Card>
    </div>
  );
}
